import { EvidenceSection } from './EvidenceSection';
import { MarkdownReportText } from './MarkdownReportText';
import { parseGenericSections, parseStandardReport } from '../../lib/evidenceReport';
import type { PublicReport } from '../../api/dataApi';

// The single-channel report body shared by PublicChannelPageScreen.tsx's
// Reports tab (always the latest report) and PublicReportScreen.tsx (one
// report by its permalink number). Three-step render fallback: today's
// fixed 6-header AI format, else whatever "## Heading" sections an owner
// edited the report into (updateReport can rename/add/drop sections
// freely - see parseGenericSections's comment), else raw markdown for
// text with no headings at all.
export function PublicReportBody({ report }: { report: PublicReport }) {
  const standard = parseStandardReport(report.text);
  const generic = standard ? null : parseGenericSections(report.text);
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-black/50 dark:text-white/50">
        {report.sender || 'AI report'}
        {report.edited && <span className="font-normal text-black/40 dark:text-white/40"> · Edited</span>}
      </span>
      {standard ? (
        <div className="flex flex-col gap-2.5">
          <EvidenceSection title="Summary" text={standard.summary} />
          <EvidenceSection title="What changed" text={standard.whatChanged} />
          <EvidenceSection title="Why it may matter" text={standard.whyItMayMatter} />
          <EvidenceSection title="Possible causes" text={standard.possibleCauses} />
          <EvidenceSection title="Evidence" text={standard.evidence} />
          <EvidenceSection title="What to watch next" text={standard.whatToWatchNext} />
        </div>
      ) : generic ? (
        <div className="flex flex-col gap-2.5">
          {generic.map((section, i) => (
            <EvidenceSection key={`${section.heading}-${i}`} title={section.heading} text={section.body} />
          ))}
        </div>
      ) : (
        <MarkdownReportText text={report.text} className="text-[15px] flex flex-col gap-1" />
      )}
      <span className="text-[11px] text-black/40 dark:text-white/40">
        {report.date ? new Date(report.date).toLocaleString() : ''}
      </span>
    </div>
  );
}
